import { FormatType, HeaderPositionType, TextStyleType } from "../constants";
import { formatter } from "../formatter";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

export class CompareValuesCategory extends BaseCategory {
    get name() {
        return "Compare Values";
    }

    get type() {
        return SlideCategories.COMPARE_VALUES;
    }

    get icon() {
        return null;
    }

    get description() {
        return "Use for comparing two or more numeric values. Do not use for more than 10 items.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Provide the data for a slide comparing numeric values. Each item should have a label and a numeric value.
        Based on the instructions in the content, provide a format for comparing the numbers (number, percent or percentage, or currency) and if currency, the currency type (USD, EUR, GBP, JPY).
        If the user explicitly asks for radial, circular, ring, or arc-style comparison, set preferredCompareLayout to "radial". Otherwise use "none".`;
    }

    getImagePrompt() {
        return false;
    }

    getPromptSchema() {
        return {
            format: "number | percent | percentage | currency",
            currencyFormat: "none | USD | EUR | GBP | JPY (only if format is currency)",
            items: [{
                label: "string",
                value: "number"
            }],
            preferredCompareLayout: "radial | none",
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    validateGeneratedModel(model) {
        if (!this.validateArray(model.items)) {
            return false;
        }
        return model.items.every(item => item && !isNaN(parseFloat(item.value)));
    }

    getSlideModels(model) {
        const normalizedModel = this.normalizeModel(model);
        const explicitRadial = this.isRadialPreferred(normalizedModel);

        // trim model items to max 10
        if (normalizedModel.items && normalizedModel.items.length > 10) {
            normalizedModel.items = normalizedModel.items.slice(0, 10);
        }

        const radialModel = this.buildRadialBarChart({
            ...normalizedModel,
            items: (normalizedModel.items || []).slice(0, 8)
        });

        const baseModels = [
            this.buildPieChart(normalizedModel, "compare_vertical_bars"),
            this.buildPieChart(normalizedModel, "compare_horizontal_bars"),
            this.buildPieChartDashboard(normalizedModel),
        ];

        if (normalizedModel.items && normalizedModel.items.length <= 4) {
            baseModels.push(this.buildPieChart(normalizedModel, "compare_circles"));
            baseModels.push(this.buildPieChart(normalizedModel, "compare_radial_bars"));
        }

        if (explicitRadial && radialModel) {
            return [radialModel, ...baseModels].filter(m => m != null);
        }

        return [...baseModels, radialModel].filter(m => m != null);
    }

    normalizeModel(model) {
        const format = this.normalizeFormat(model?.format);
        const items = (model?.items || [])
            .map(item => ({
                label: item?.label ?? "",
                value: parseFloat(item?.value)
            }))
            .filter(item => !isNaN(item.value));

        let normalizedItems = items;
        if (format === FormatType.PERCENT) {
            const looksFractional = items.length > 0 && items.every(item => Math.abs(item.value) <= 1);
            if (looksFractional) {
                normalizedItems = items.map(item => ({
                    ...item,
                    value: item.value * 100
                }));
            }
        }

        return {
            ...model,
            format,
            items: normalizedItems
        };
    }

    normalizeFormat(format) {
        const lower = `${format || ""}`.trim().toLowerCase();
        if (lower === "percent" || lower === "percentage") {
            return FormatType.PERCENT;
        }
        if (lower === "currency") {
            return FormatType.CURRENCY;
        }
        if (lower === "number") {
            return FormatType.NUMBER;
        }
        return FormatType.NUMBER;
    }

    isRadialPreferred(model) {
        const raw = `${model?.preferredCompareLayout || ""}`.toLowerCase().trim();
        if (raw === "radial") {
            return true;
        }
        return /(radial|circular|circle|ring|arc)/.test(raw);
    }

    getFormatOptions(model) {
        const options = {
            abbreviated: "lower"
        };
        if (model.format === FormatType.CURRENCY) {
            options.currency = formatter.parseCurrencySymbol(model.currencyFormat || "USD");
        }
        return options;
    }

    buildPieChart(model, templateId = "compare_vertical_bars") {
        let builder = new SlideModelBuilder(templateId, model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });
        builder.createPrimary({
            collectionColor: "colorful",
            format: builder.validateEnum(model.format, FormatType, FormatType.NUMBER),
            formatOptions: this.getFormatOptions(model),
            items: model.items?.map(item => ({
                value: item.value,
                text: builder.createSingleBlockText(item.label, TextStyleType.TITLE)
            })) || [],
        });
        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }
        return builder.model;
    }

    buildPieChartDashboard(model) {
        let builder = new SlideModelBuilder("dashboard", model);

        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.LEFT });

        builder.createPrimary({
            items: [{
                componentType: "PieChart",
                childElement: {
                    collectionColor: "theme-to-dark",
                    data: model.items.map(item => ({ value: item.value, label: builder.createSingleBlockText(item.label) })),
                    format: "percent",
                    innerRadius: 0,
                    isDonut: false,
                    labelFormat: "percentage",
                    sortValues: true
                }
            }]
        });
        return builder.model;
    }

    buildRadialBarChart(model) {
        if (!model?.items?.length || model.items.length > 8) {
            return null;
        }

        let builder = new SlideModelBuilder("radial_bar_chart", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        const values = model.items.map(item => item.value);
        const maxValue = model.format === FormatType.PERCENT
            ? Math.max(100, ...values)
            : Math.max(1, ...values);

        builder.createPrimary({
            collectionColor: "theme-to-light",
            trackStyle: "bar",
            startPosition: "top",
            minValue: 0,
            maxValue,
            format: model.format,
            formatOptions: this.getFormatOptions(model),
            items: model.items.map(item => ({
                value: item.value,
                label: builder.createSingleBlockText(item.label, TextStyleType.BODY)
            })),
        });

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }
        return builder.model;
    }
}
